import capitalize from 'lodash/capitalize.js';
import cloneDeep from 'lodash/cloneDeep.js';
import has from 'lodash/has.js';
import isEmpty from 'lodash/isEmpty.js';
import merge from 'lodash/merge.js';

import Quiz from '@/plugins/autosaver/quizzes/quizzes.js';
import axios from '@/plugins/axios.js';
import { DialogService } from '@/services/DialogService.js';
import Item from '@/store/quill/Item.js';
import QuizBuilder from '@/store/utilities/QuizBuilder.js';
export default merge(Item(), {
    autoImport: false,

    state() {
        return {
            id: null,
            must_answer_in_order: false,
            calculator_enabled: true,
            questions: [],
            question_count: null,
            questions_per_page: null,
            random_question_order: false,
            status: {
                display: false,
                message: null,
            },
            title: null,
            uuid: null,
            type: null,
            required: null,
            is_repetitive: null,
            activity_uuid: null,
            quiz_version_id: null,
            quiz_version_number: null,
            original_version_id: null,
            parent: null,
            groups: [],
            course_codes: [],
            has_quiz_attempt: null,
            points_possible: null,
        };
    },

    getters: {
        answer:
            state =>
            ({ field, order, parent }) => {
                if (!state.questions[parent] || !state.questions[parent].answers.length) return null;

                const answer = state.questions[parent].answers[order];
                if (!answer) return null;

                if (!field) return answer;

                return answer[field];
            },

        question:
            state =>
            ({ order, field }) => {
                if (!state.questions.length) return null;

                if (!field) return state.questions[order];

                return state.questions[order][field];
            },

        validate:
            (state, getters) =>
            ({ target, type }) => {
                const helper = new QuizBuilder({ quizType: state.type });

                const item = getters.question({ order: target });

                return helper.validate({ type, item });
            },

        hasUnsavedQuestions: state => () => {
            return state.questions.some(question => !question.id);
        },
    },

    actions: {
        addQuestion: async (context, { type }) => {
            const index = context.state.questions.length;

            const question = {
                answers: [],
                body: null,
                rationale: null,
                hint: null,
                id: null,
                display_order: index,
                expanded: false,
                quiz_id: context.state.id,
                random_answer_order: false,
                partial_credit: false,
                points: null,
                type,
            };

            context.commit('ADD_QUESTION', { question });

            context.dispatch('hydrateQuestionAnswers', { order: index, type });
        },

        addAnswer: (context, { target }) => {
            const answers = context.getters.question({
                order: target,
                field: 'answers',
            });
            const index = answers.length;

            const answer = {
                body: null,
                display_order: index,
                is_correct: false,
                parent: target,
                value: 0,
                question_id: null,
            };

            context.commit('ADD_ANSWER', { payload: answers, answer });
        },

        hydrateQuestions: (context, { questions }) => {
            if (questions.some(item => !item.display_order && item.display_order !== 0)) {
                questions.forEach((item, i) => {
                    item.display_order = i;

                    if (item.answers.some(answer => !answer.display_order)) {
                        item.answers.forEach((answer, index) => {
                            answer.display_order = index;
                        });
                    }
                });
            }

            questions.sort((a, b) => (a.display_order > b.display_order ? 1 : -1));

            return questions.forEach((question, index) => {
                question.expanded = false;

                question.answers.forEach(answer => {
                    answer.parent = index;
                    answer.display_order = String(answer.display_order);

                    if (question.type === 'free_form') {
                        answer.type = 'free_form';
                    }
                });

                context.commit('ADD_QUESTION', { question });
            });
        },

        hydrateQuestionAnswers: (context, { order, type }) => {
            const helper = new QuizBuilder({ parent: order });

            let answers;

            if (type.includes('true')) {
                answers = helper.trueFalseAnswers({ parent: order });
            }

            if (type.includes('multiple')) {
                answers = helper.multipleChoiceAnswers({ parent: order });
            }

            if (type.includes('free_form')) {
                answers = helper.freeFormAnswers({ parent: order });
            }

            context.commit('SET_QUESTION_FIELD', {
                order,
                field: 'answers',
                value: answers,
            });
        },

        confirmRemoval: (context, { parent = null, type, target, store = null }) => {
            const label = type.includes('ans') ? 'answer' : 'question';

            DialogService.confirm({
                type: 'is-danger',
                title: `Remove ${capitalize(label)}`,
                message: `<p>Are you sure you want to <strong>remove</strong> this ${label}?</p> </br> <p>This action cannot be <u>undone</u>.</p>`,
                confirmText: `Remove ${capitalize(label)}`,
                cancelText: 'Go Back',
                onConfirm: async () => {
                    await context.dispatch('remove', {
                        parent,
                        type,
                        target,
                        store,
                    });
                },
                scroll: 'keep',
                focusOn: 'cancel',
                trapFocus: true,
            });
        },

        remove: async (context, { parent = null, type, target, store = null }) => {
            let data;
            const items = type === 'answers' ? context.state.questions[parent].answers : context.state.questions;
            const item = cloneDeep(items[target]);
            const hasId = has(item, 'id') && !!item.id;

            if (type === 'questions') {
                await context.commit('REMOVE_QUESTION', { target });
            }

            if (type === 'answers') {
                await context.commit('REMOVE_ANSWER', { target, parent, item });
            }

            if (!isEmpty(items)) {
                await context.commit('REORDER', {
                    payload: items,
                    field: 'display_order',
                });
            }

            if (hasId && item.id && type === 'questions') {
                data = await axios.delete(`/api/questions/${item.id}`).then(resp => resp.data);
            }

            if (hasId && item.id && type === 'answers') {
                const question = context.getters.question({
                    order: item.parent,
                });

                data = await axios.put(`/api/questions/${question.id}`, question).then(resp => resp.data);
            }

            if (data?.meta && data.meta?.current_version) {
                const helper = new Quiz(store, { payload: { target: '' } });
                const versionId = Number(data.meta.current_version.versionable_id);
                const quizId = Number(context.state.quiz_version_id);

                if (versionId !== quizId) {
                    await helper.updateVersion({
                        meta: data.meta.current_version,
                        quiz: context.state,
                    });
                }
            }
        },

        reorder: async (context, { field, parent = null, payload, type, query = null }) => {
            const allQuestionsHaveId = payload.every(item => item.id);

            await context.commit('REORDER', { payload, field });

            if (allQuestionsHaveId) {
                context.commit(
                    'QuizBuilder/AUTO_SAVE_REORDER',
                    {
                        field,
                        query: query || 'duplication=false',
                        parent,
                        target: context.state.uuid,
                        type,
                    },
                    { root: true }
                );
            }
        },

        setAnswerField: (context, { field, order, parent, value }) => {
            context.commit('SET_ANSWER_FIELD', { field, order, parent, value });

            context.commit(
                'QuizBuilder/AUTO_SAVE_ANSWER',
                {
                    target: context.state.uuid,
                    field,
                    parent,
                },
                { root: true }
            );
        },

        setCorrectAnswer: (context, { payload, type, parent }) => {
            const helper = new QuizBuilder({ store: context });
            const answers = context.getters.question({
                order: parent,
                field: 'answers',
            });

            if (!type.includes('answer')) {
                helper.setSingleCorrectAnswer(payload, answers);
            } else {
                helper.setMultipleCorrectAnswers(payload, answers, parent);
            }

            context.commit(
                'QuizBuilder/AUTO_SAVE_ANSWER',
                {
                    target: context.state.uuid,
                    field: 'is_correct',
                    parent,
                },
                { root: true }
            );
        },

        setQuestionField: (context, { field, order, value }) => {
            context.commit('SET_QUESTION_FIELD', { order, field, value });

            context.commit(
                'QuizBuilder/AUTO_SAVE_QUESTION',
                { target: context.state.uuid, field, order },
                { root: true }
            );
        },

        setQuizField: (context, { field, value }) => {
            context.commit('SET_QUIZ_FIELD', { field, value });

            context.commit('QuizBuilder/AUTO_SAVE_QUIZ', { target: context.state.uuid, field }, { root: true });
        },

        getQuizActivityCount: async ({ state }) => {
            const { id } = state;
            const type = state.type ? state.type : 'quiz';
            const route = state.type ? 'surveys' : 'quizzes';

            const data = await axios.get(`/api/${route}/${id}/get-${type}-activity-count`).then(resp => resp?.data);

            return data.quiz_activity_count;
        },

        getAllActivities: ({ state }) => {
            const type = state.type ? 'surveys' : 'quizzes';
            const { id } = state;

            return axios.get(`/api/${type}/${id}/get-all-versions`).then(resp => resp?.data?.data);
        },

        getAllVersions: ({ state }) => {
            const type = state.type ? 'surveys' : 'quizzes';
            const { id } = state;
            const data = axios.get(`/api/${type}/${id}/get-all-versions`).then(resp => resp?.data?.versions);

            return data;
        },

        getCurrentState: ({ state }) => {
            const type = state.type ? 'surveys' : 'quizzes';
            const { id } = state;
            const data = axios.get(`/api/${type}/${id}/get-all-versions`).then(resp => resp?.data);

            return data;
        },

        previewVersion: async ({ state }, payload) => {
            const type = state.type ? state.type : 'quiz';

            await axios.post(
                `/api/${type}-activity/${payload.activity.contentable_id}/${type}-activity-update-version`,
                {
                    quiz_activity_id: payload.activity.contentable_id,
                    title: payload.activity.title,
                    preview: state.id,
                }
            );
        },

        updateSelected: async ({ state }, payload) => {
            const type = state.type ? state.type : 'quiz';

            await Promise.all(
                payload.map(async item => {
                    const id = item.activity.contentable_id;

                    await axios.post(`/api/${type}-activity/${id}/${type}-activity-update-version`, {
                        quiz_activity_id: id,
                        title: item.activity.title,
                    });
                })
            );
        },
    },

    mutations: {
        ADD_QUESTION: (state, { question }) => {
            state.questions.push(question);
        },

        ADD_ANSWER: (state, { answer, payload }) => {
            payload.push(answer);
        },

        TOGGLE_STATUS: (state, { message }) => {
            state.status.display = !state.status.display;
            state.status.message = message;
        },

        REMOVE: (state, { payload, target }) => {
            delete payload[target];
        },

        REMOVE_QUESTION: (state, { target }) => {
            state.questions.splice(target, 1);
        },

        REMOVE_ANSWER: (state, { target, parent, item }) => {
            state.questions[parent].answers = state.questions[parent].answers.filter(answer => {
                return answer.display_order !== item.display_order;
            });
        },

        REORDER: (state, { field }) => {
            const payload = state.questions;

            payload.forEach((item, index) => {
                item[field] = String(index);

                if (has(item, 'answers') && !isEmpty(item.answers)) {
                    item.answers.forEach((answer, answerIndex) => {
                        answer.parent = index;
                        answer[field] = answerIndex;
                    });
                }
            });
        },

        SET_QUIZ_FIELD: (state, { field, value }) => {
            state[field] = value;
        },

        SET_QUESTION_FIELD: (state, { field, order, value }) => {
            state.questions[order][field] = value;
        },

        SET_ANSWER_FIELD: (state, { field, order, parent, value }) => {
            state.questions[parent].answers[order][field] = value;
        },
    },
});
